import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../i18n/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../data/DataContext';
import { useApi } from '../../api/client';
import { recentActivity } from '../../api/notifications';
import { formatRelativeTime } from '../../utils/format';
import Button from '../ui/Button';

const DOT_COLORS = {
  green: 'var(--success)',
  gold: 'var(--gold)',
  blue: 'var(--blue-light)',
  red: 'var(--red-accent)',
};

export default function NotificationsDrawer({ isOpen, onClose }) {
  const t = useT();
  const navigate = useNavigate();
  const { activity: mockActivity } = useData();
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('modal-open');
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    if (useApi) {
      setLoading(true);
      recentActivity()
        .then((data) => setItems(
          (data || []).map((a) => ({
            id: a.id,
            text: a.description || a.action,
            time: a.at,
            dot: a.action.includes('DELETED') ? 'red' : a.action.includes('PAYMENT') ? 'gold' : 'green',
          }))
        ))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    } else {
      setItems(
        (mockActivity || []).map((a) => ({
          id: a.id,
          textKey: a.textKey,
          params: a.params,
          time: a.at,
          dot: a.dot,
        }))
      );
    }
  }, [isOpen, mockActivity]);

  if (!isOpen) return null;

  const node = (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={t('notifications.title')}>
        <div className="drawer-header">
          <div className="drawer-title">{t('notifications.title')}</div>
          <button type="button" className="modal-close" aria-label={t('common.close')} onClick={onClose}>×</button>
        </div>
        <div className="drawer-body">
          {loading && <div className="empty-state__body">{t('common.loading')}</div>}
          {!loading && items.length === 0 && (
            <div className="empty-state__body">{t('notifications.empty')}</div>
          )}
          {items.map((item) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 0', borderBottom: '1px solid rgba(226,221,212,0.5)' }}>
              <div style={{
                width: '7px', height: '7px', borderRadius: '50%', marginTop: '6px',
                flexShrink: 0, background: DOT_COLORS[item.dot] || 'var(--gold)',
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', color: 'var(--text)', lineHeight: '1.45' }}>
                  {item.textKey ? t(item.textKey, item.params) : item.text}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px', fontStyle: 'italic' }}>
                  {formatRelativeTime(item.time, t)}
                </div>
              </div>
            </div>
          ))}
        </div>
        {useApi && hasPermission('audit:view') && (
          <div className="drawer-footer">
            <Button variant="ghost" size="sm" onClick={() => { onClose?.(); navigate('/audit'); }}>
              {t('notifications.view_all')}
            </Button>
          </div>
        )}
      </aside>
    </>
  );

  return createPortal(node, document.body);
}
